import { useAuth } from '../App';
import { NavLink, useNavigate } from 'react-router-dom';
import { t, LANGS } from '../i18n';
import LiveCostMeter from './LiveCostMeter';
import {
  MessageSquare, FileText, GanttChart, Inbox, Activity,
  LayoutDashboard, Settings2, UploadCloud, FileBarChart2, ShieldCheck, LogOut, Zap,
} from 'lucide-react';

const NAV = {
  user: [
    { to: '/ask', icon: MessageSquare, key: 'ask' },
    { to: '/documents', icon: FileText, key: 'documents' },
  ],
  dept_head: [
    { to: '/dept/dashboard', icon: LayoutDashboard, key: 'dept_dashboard' },
    { to: '/ask', icon: MessageSquare, key: 'ask' },
    { to: '/dept/faqs', icon: Inbox, key: 'dept_faqs' },
    { to: '/dept/audit', icon: Activity, key: 'dept_audit' },
    { to: '/documents', icon: FileText, key: 'documents' },
  ],
  super_admin: [
    { to: '/admin/dashboard', icon: LayoutDashboard, key: 'admin_dashboard' },
    { to: '/ask', icon: MessageSquare, key: 'ask' },
    { to: '/admin/routing', icon: Settings2, key: 'admin_routing' },
    { to: '/admin/documents', icon: UploadCloud, key: 'admin_documents' },
    { to: '/dept/faqs', icon: Inbox, key: 'dept_faqs' },
    { to: '/dept/audit', icon: Activity, key: 'dept_audit' },
    { to: '/admin/roi', icon: FileBarChart2, key: 'admin_roi' },
    { to: '/admin/audit', icon: ShieldCheck, key: 'admin_audit' },
  ],
};

export default function AppShell({ children }) {
  const { auth, setAuth, lang, setLanguage } = useAuth();
  const nav = useNavigate();
  const items = NAV[auth.user.role] || NAV.user;

  const logout = () => {
    setAuth(null);
    nav('/login', { replace: true });
  };

  const roleColor = {
    user: 'text-slate-300',
    dept_head: 'text-sky-300',
    super_admin: 'text-[#DDA079]',
  }[auth.user.role];

  return (
    <div className="min-h-screen flex grain" data-testid="app-shell">
      {/* Sidebar */}
      <aside className="w-[260px] border-r border-white/10 bg-[#0A0A0C] flex flex-col fixed h-screen z-10">
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-[#C17F59] pulse-dot rounded-full" />
            <span className="data-label">Vedanta Group</span>
          </div>
          <div className="mt-2 font-headings text-xl text-white">Document Intelligence</div>
          <div className="data-label mt-1">{t(lang, 'company')}</div>
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {items.map(({ to, icon: Icon, key }) => (
            <NavLink
              key={to + key}
              to={to}
              data-testid={`nav-${key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 mb-0.5 text-[13px] transition-colors ` +
                (isActive
                  ? 'bg-white/[0.04] text-white border-l-2 border-[#C17F59]'
                  : 'text-slate-400 hover:text-white hover:bg-white/[0.02] border-l-2 border-transparent')
              }
            >
              <Icon size={15} strokeWidth={1.6} />
              <span>{t(lang, `nav.${key}`)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-gradient-to-br from-[#C17F59] to-[#A66A45] flex items-center justify-center text-xs font-semibold rounded-sm">
              {auth.user.full_name.split(' ').map(n => n[0]).slice(0,2).join('')}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-white truncate">{auth.user.full_name}</div>
              <div className={`text-[10px] font-mono uppercase tracking-wider ${roleColor}`}>
                {t(lang, `role.${auth.user.role}`)} · {auth.user.department}
              </div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full btn-ghost flex items-center gap-2 text-xs"
          >
            <LogOut size={13} /> {t(lang, 'signOut')}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-[260px] min-w-0">
        {/* Topbar */}
        <div className="sticky top-0 z-10 backdrop-blur-xl bg-[#0A0A0C]/80 border-b border-white/10 px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Zap size={14} className="text-[#C17F59] flex-shrink-0" />
            <span className="data-label text-emerald-400">● LIVE</span>
          </div>
          <div className="flex items-center gap-3">
            <LiveCostMeter />
            <div className="flex items-center gap-1 border border-white/10 rounded-sm overflow-hidden" data-testid="language-toggle">
              {LANGS.map(L => (
                <button
                  key={L.code}
                  data-testid={`lang-${L.code}`}
                  onClick={() => setLanguage(L.code)}
                  className={
                    `px-3 py-1 text-[11px] font-mono uppercase tracking-wider transition-colors ` +
                    (lang === L.code ? 'bg-[#C17F59] text-white' : 'text-slate-400 hover:text-white hover:bg-white/[0.04]')
                  }
                  title={L.native}
                >
                  {L.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="px-8 py-8 max-w-[1400px]">{children}</div>
      </div>
    </div>
  );
}
